import { MouseEvent, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Box, Menu, MenuItem } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBuilding, faChevronDown, faInfoCircle } from '@fortawesome/free-solid-svg-icons';

import { DispatchProperties, useSelector } from '../../redux/store';
import { LoadingOverlay, Text } from '../../shared/uiComponents';
import { BranchProperties } from '../../redux/API/ClientAPIHelpers/branchProperties';
import { getBranchId } from '../../redux/API/Main/helpers';
import { getUserBranches } from '../../redux/State/userSlice';
import { setBranchId } from '../../redux/API/Main/fetchAPI';
import { BRANCH_INFO_TEXT, formatBranchName } from '../../shared/Helpers/constants';
import { PEARL, PRIMARY, STROKE } from '../../shared/Helpers/colors';

const BranchFilter = () => {
	const dispatch = useDispatch<DispatchProperties>();
	const navigate = useNavigate();
	const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
	const [branchName, setBranchName] = useState<string>('');
	const [isSwitching, setIsSwitching] = useState<boolean>(false);
	const [showAll, setShowAll] = useState<boolean>(false);

	const branches = useSelector((state) => state.userBranch.userBranches);
	const userId = useSelector((state) => state.account.user.id);

	const branchId = getBranchId();
	const isDisabled = branches.length === 1;

	useEffect(() => {
		if (!userId) return;
		dispatch(getUserBranches(userId));
	}, [userId, dispatch]);

	useEffect(() => {
		if (!branches?.length) return;

		if (!branchId) {
			const firstBranch = branches[0];
			if (!firstBranch) return;
			setBranchId(firstBranch.id);
			setBranchName(formatBranchName(firstBranch.state));
			setShowAll(false);

			return;
		}

		const branch = branches.find((singleBranch) => singleBranch.id === branchId);
		if (!branch) return;
		setBranchName(formatBranchName(branch.state));
		setShowAll(false);
	}, [branches, branchId]);

	const handleClick = (event: MouseEvent<HTMLElement>) => {
		if (isDisabled) return;
		setAnchorEl(event.currentTarget);
	};

	const handleClose = () => {
		setAnchorEl(null);
	};

	const onBranchChange = (data: BranchProperties | null) => {
		if (!data) {
			// "All" option selected
			handleClose();
			setShowAll(true);
			setBranchName('All');

			return;
		}

		const { id, state } = data;

		if (branchId === id && !showAll) {
			handleClose();

			return;
		}

		handleClose();
		setIsSwitching(true);
		setBranchId(id);
		setBranchName(formatBranchName(state));
		setShowAll(false);

		navigate('/dashboard');

		setTimeout(() => {
			window.location.reload();
		}, 100);
	};

	return (
		<>
			<Box
				onClick={handleClick}
				sx={{
					display: 'flex',
					alignItems: 'center',
					gap: '8px',
					padding: '4px 8px 4px 4px',
					height: '40px',
					backgroundColor: 'white',
					border: `1px solid ${STROKE.TERTIARY}`,
					borderRadius: '9px',
					cursor: isDisabled ? 'not-allowed' : 'pointer',
					opacity: isDisabled ? 0.6 : 1,
					transition: 'all 0.2s ease',
					'&:hover': {
						backgroundColor: isDisabled ? 'white' : PEARL.QUATERNARY,
					},
				}}
			>
				<Box
					sx={{
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'center',
						width: '32px',
						height: '32px',
						backgroundColor: PEARL.TERTIARY,
						borderRadius: '8px',
						flexShrink: 0,
					}}
				>
					<FontAwesomeIcon icon={faBuilding} color={PEARL.BASE[700]} fontSize={14} />
				</Box>
				<Box
					sx={{
						display: 'flex',
						flexDirection: 'column',
						justifyContent: 'center',
						gap: 0,
					}}
				>
					<Text
						title="Branch"
						type="pxs"
						style={{
							fontSize: '10px',
							lineHeight: '12px',
							color: 'rgba(0, 0, 0, 0.6)',
							margin: 0,
						}}
					/>
					<Text
						title={branchName}
						type="psm"
						style={{
							fontSize: '14px',
							lineHeight: '20px',
							fontWeight: 500,
							color: 'rgba(0, 0, 0, 0.72)',
							margin: 0,
							whiteSpace: 'nowrap',
						}}
					/>
				</Box>
				<FontAwesomeIcon
					icon={faChevronDown}
					color={PEARL.BASE[400]}
					fontSize={12}
					style={{ marginLeft: '4px', flexShrink: 0 }}
				/>
			</Box>
			<Menu
				anchorEl={anchorEl}
				open={Boolean(anchorEl)}
				onClose={handleClose}
				anchorOrigin={{
					vertical: 'bottom',
					horizontal: 'right',
				}}
				transformOrigin={{
					vertical: 'top',
					horizontal: 'right',
				}}
				slotProps={{
					paper: {
						sx: {
							width: '200px',
							borderRadius: '9px',
							boxShadow: '0px 12px 16px -4px rgba(0, 0, 0, 0.08), 0px 4px 6px -4px rgba(0, 0, 0, 0.08)',
							padding: '4px',
							marginTop: '8px',
						},
					},
				}}
			>
				{/* {hasMultipleBranches && (
          <MenuItem
            onClick={() => onBranchChange(null)}
            selected={showAll}
            disabled={isSwitching}
            sx={{
              borderRadius: "8px",
              fontSize: "14px",
              fontWeight: 500,
              color: "rgba(0, 0, 0, 0.72)",
              borderBottom: `1px solid ${STROKE.SECONDARY}`,
              "&:hover": {
                backgroundColor: PEARL.QUATERNARY,
              },
            }}
          >
            All
          </MenuItem>
        )} */}
				{branches.map((branch) => (
					<MenuItem
						key={branch.id}
						onClick={() => onBranchChange(branch)}
						selected={branchId === branch.id && !showAll}
						disabled={isSwitching}
						sx={{
							borderRadius: '8px',
							fontSize: '14px',
							fontWeight: 500,
							color: 'rgba(0, 0, 0, 0.72)',
							borderBottom: `1px solid ${STROKE.SECONDARY}`,
							'&:hover': {
								backgroundColor: PEARL.QUATERNARY,
							},
							'&:last-child': {
								borderBottom: 'none',
							},
						}}
					>
						{formatBranchName(branch.state)}
					</MenuItem>
				))}
				<Box
					sx={{
						display: 'flex',
						alignItems: 'flex-start',
						gap: '8px',
						padding: '8px 12px',
						backgroundColor: PRIMARY.QUATERNARY,
						borderRadius: '7px',
						marginTop: '4px',
					}}
				>
					<FontAwesomeIcon
						icon={faInfoCircle}
						color={PRIMARY.BASE}
						fontSize={14}
						style={{ marginTop: '2px', flexShrink: 0 }}
					/>
					<Text
						title={BRANCH_INFO_TEXT}
						type="pxs"
						style={{
							fontSize: '12px',
							lineHeight: '16px',
							color: 'rgba(0, 0, 0, 0.72)',
							flex: 1,
						}}
					/>
				</Box>
			</Menu>

			<LoadingOverlay open={isSwitching} title={`Switching to ${branchName}...`} />
		</>
	);
};

export default BranchFilter;
